type SignUpMethod = "email" | "google";
type AnalyticsData = Record<string, string | number | boolean>;

export type AnalyticsConsent = "granted" | "denied";
export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    umami?: {
      track(name: string, data?: AnalyticsData): void;
    };
  }
}

const measurementId = (
  import.meta.env.VITE_GA_MEASUREMENT_ID ??
  import.meta.env.VITE_GA4_MEASUREMENT_ID
)?.trim();
const productionCookieDomain = "kinderwens-dating.nl";
const productionAnalyticsHosts = new Set([
  "kinderwens-dating.nl",
  "www.kinderwens-dating.nl",
  "app.kinderwens-dating.nl",
]);

const sharedConsentCookieName = "kwd_cookie_consent_v1";
const legacyConsentStorageKey = "kinderwens-analytics-consent";
const consentCookieMaxAgeSeconds = 365 * 24 * 60 * 60;
let initialized = false;

export function getAnalyticsConsent(): AnalyticsConsent | null {
  const preferences = getConsentPreferences();
  if (!preferences) return null;
  return preferences.analytics ? "granted" : "denied";
}

export function getConsentPreferences(): ConsentPreferences | null {
  if (typeof window === "undefined") return null;

  const cookiePreferences = readSharedConsentCookie();
  if (cookiePreferences) return cookiePreferences;

  const storedConsent = readLegacyConsentStorage();
  if (storedConsent) {
    const preferences = {
      analytics: storedConsent === "granted",
      marketing: false,
    };
    writeSharedConsentCookie(preferences);
    return preferences;
  }
  return null;
}

export function initAnalytics(): void {
  if (
    initialized ||
    !measurementId ||
    typeof window === "undefined" ||
    !isProductionAnalyticsHost()
  ) {
    return;
  }
  initialized = true;

  window.dataLayer ??= [];
  window.gtag ??= (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (getAnalyticsConsent() === "granted") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
    });
  }

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    ...(usesProductionCookieDomain()
      ? { cookie_domain: productionCookieDomain }
      : {}),
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function setAnalyticsConsent(consent: AnalyticsConsent): void {
  const current = getConsentPreferences();
  setConsentPreferences({
    analytics: consent === "granted",
    marketing: current?.marketing ?? false,
  });
}

export function setConsentPreferences(
  preferences: ConsentPreferences,
): void {
  if (typeof window === "undefined") return;
  writeSharedConsentCookie(preferences);
  writeLegacyConsentStorage(preferences.analytics ? "granted" : "denied");
  initAnalytics();
  window.gtag?.("consent", "update", {
    analytics_storage: preferences.analytics ? "granted" : "denied",
  });
  if (!preferences.analytics) {
    clearAnalyticsCookies();
  }
  window.dispatchEvent(
    new CustomEvent<ConsentPreferences>(analyticsConsentChangedEvent, {
      detail: preferences,
    }),
  );
}

export function trackSignUp(method: SignUpMethod): void {
  trackEvent("sign_up", { method });
}

/**
 * Send a privacy-safe event to Replit's injected Umami tracker and GA4.
 * Analytics is opt-in and a missing/broken tracker must never affect the app.
 */
export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === "undefined" || getAnalyticsConsent() !== "granted") return;

  try {
    window.umami?.track(name, data);
  } catch {
    // Analytics must never break the app.
  }

  if (!isProductionAnalyticsHost() || !measurementId) return;

  try {
    initAnalytics();
    window.gtag?.("event", name, {
      ...data,
      transport_type: "beacon",
    });
  } catch {
    // Analytics must never break the app.
  }
}

function clearAnalyticsCookies(): void {
  const analyticsCookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));

  const cookieDomains = new Set([
    window.location.hostname,
    productionCookieDomain,
  ]);

  for (const name of analyticsCookieNames) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    for (const domain of cookieDomains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}; SameSite=Lax`;
    }
  }
}

function usesProductionCookieDomain(): boolean {
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === productionCookieDomain ||
    hostname.endsWith(`.${productionCookieDomain}`)
  );
}

function isProductionAnalyticsHost(): boolean {
  if (typeof window === "undefined") return false;
  return productionAnalyticsHosts.has(window.location.hostname.toLowerCase());
}

function readSharedConsentCookie(): ConsentPreferences | null {
  if (typeof document === "undefined") return null;

  const prefix = `${sharedConsentCookieName}=`;
  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  if (!cookie) return null;

  try {
    const parsed = JSON.parse(
      decodeURIComponent(cookie.slice(prefix.length)),
    ) as {
      version?: unknown;
      categories?: { analytics?: unknown; marketing?: unknown };
    };
    if (
      parsed.version !== 1 ||
      typeof parsed.categories?.analytics !== "boolean"
    ) {
      return null;
    }
    return {
      analytics: parsed.categories.analytics,
      marketing:
        typeof parsed.categories.marketing === "boolean"
          ? parsed.categories.marketing
          : false,
    };
  } catch {
    return null;
  }
}

function writeSharedConsentCookie(preferences: ConsentPreferences): void {
  if (typeof document === "undefined") return;

  const value = encodeURIComponent(
    JSON.stringify({
      version: 1,
      timestamp: new Date().toISOString(),
      categories: {
        necessary: true,
        analytics: preferences.analytics,
        marketing: preferences.marketing,
      },
      source: "js",
    }),
  );
  const attributes = [
    `${sharedConsentCookieName}=${value}`,
    `Max-Age=${consentCookieMaxAgeSeconds}`,
    "Path=/",
    "SameSite=Lax",
  ];

  if (usesProductionCookieDomain()) {
    attributes.push(`Domain=.${productionCookieDomain}`);
  }
  if (window.location.protocol === "https:") {
    attributes.push("Secure");
  }

  try {
    document.cookie = attributes.join("; ");
  } catch {
    // localStorage remains available as a fallback where cookies are blocked.
  }
}

function readLegacyConsentStorage(): AnalyticsConsent | null {
  try {
    const storedConsent = window.localStorage.getItem(legacyConsentStorageKey);
    return storedConsent === "granted" || storedConsent === "denied"
      ? storedConsent
      : null;
  } catch {
    return null;
  }
}

function writeLegacyConsentStorage(consent: AnalyticsConsent): void {
  try {
    window.localStorage.setItem(legacyConsentStorageKey, consent);
  } catch {
    // The shared first-party cookie is the primary persistence mechanism.
  }
}

export const openAnalyticsSettingsEvent = "open-analytics-settings";

export function openAnalyticsSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(openAnalyticsSettingsEvent));
}

export const analyticsConsentChangedEvent = "analytics-consent-changed";
